<template>
  <div class="competition-detail-container">
    <!-- Background Effects -->
    <div class="bg-effects">
      <div class="glow-orb glow-orb-1"></div>
      <div class="glow-orb glow-orb-2"></div>
      <div class="glow-orb glow-orb-3"></div>
    </div>

    <!-- Main Content -->
    <div class="content-wrapper">
      <!-- Back Button -->
      <button class="back-btn" @click="$router.go(-1)">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M19 12H5M12 19l-7-7 7-7"/>
        </svg>
        Back
      </button>

      <!-- Hero Section -->
      <div class="hero-section">
        <div class="hero-badge">
          <span class="badge-dot" :class="getStatusClass(competitionDetails?.competition_status)"></span>
          {{ competitionDetails?.competition_status || 'Loading...' }}
        </div>
        <h1 class="hero-title">{{ competitionDetails?.title || 'Competition Details' }}</h1>
        <p class="hero-description">{{ competitionDetails?.description || 'No description available' }}</p>
        
        <div class="hero-stats">
          <div class="stat-item">
            <div class="stat-icon trophy">🏆</div>
            <div>
              <div class="stat-value">{{ competitionDetails?.target_points || 0 }}</div>
              <div class="stat-label">Target Points</div>
            </div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-icon teams">👥</div>
            <div>
              <div class="stat-value">{{ competitionTeams.length }}</div>
              <div class="stat-label">Teams</div>
            </div>
          </div>
          <div class="stat-divider"></div>
          <div class="stat-item">
            <div class="stat-icon date">📅</div>
            <div>
              <div class="stat-value">{{ formatDate(competitionDetails?.start_date) }}</div>
              <div class="stat-label">Start Date</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Date Range Card -->
      <div class="date-range-card">
        <div class="date-range-item">
          <span class="date-label">📅 Start</span>
          <span class="date-value">{{ formatDate(competitionDetails?.start_date) }}</span>
        </div>
        <div class="date-range-arrow">→</div>
        <div class="date-range-item">
          <span class="date-label">📅 End</span>
          <span class="date-value">{{ formatDate(competitionDetails?.end_date) }}</span>
        </div>
        <div class="date-range-progress">
          <div class="progress-track">
            <div class="progress-fill" :style="{ width: getProgressPercentage() + '%' }"></div>
          </div>
          <span class="progress-label">{{ getProgressPercentage() }}% Complete</span>
        </div>
      </div>

      <!-- Teams Section -->
      <div class="teams-section">
        <div class="section-header">
          <div class="header-left">
            <h2 class="section-title">Participating Teams</h2>
            <span class="section-count">{{ competitionTeams.length }} Teams</span>
          </div>
          <div class="header-right">
            <div class="search-box">
              <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="M21 21l-4.35-4.35"/>
              </svg>
              <input 
                v-model="search" 
                type="text" 
                placeholder="Search teams..." 
                class="search-input"
              />
            </div>
            <button class="view-toggle-btn" @click="toggleView">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect v-if="viewMode === 'grid'" x="3" y="3" width="7" height="7" rx="1"/>
                <rect v-if="viewMode === 'grid'" x="14" y="3" width="7" height="7" rx="1"/>
                <rect v-if="viewMode === 'grid'" x="3" y="14" width="7" height="7" rx="1"/>
                <rect v-if="viewMode === 'grid'" x="14" y="14" width="7" height="7" rx="1"/>
                <rect v-if="viewMode === 'list'" x="3" y="3" width="18" height="4" rx="1"/>
                <rect v-if="viewMode === 'list'" x="3" y="10" width="18" height="4" rx="1"/>
                <rect v-if="viewMode === 'list'" x="3" y="17" width="18" height="4" rx="1"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Loading State -->
        <div v-if="loading" class="loading-grid">
          <div v-for="i in 4" :key="i" class="team-skeleton">
            <div class="skeleton-avatar"></div>
            <div class="skeleton-line"></div>
            <div class="skeleton-line short"></div>
            <div class="skeleton-line half"></div>
          </div>
        </div>

        <!-- Empty State -->
        <div v-else-if="!formattedCompetitionTeams.length" class="empty-state">
          <div class="empty-icon">🤝</div>
          <h3>No Teams Participating</h3>
          <p>This competition doesn't have any participating teams yet.</p>
        </div>

        <!-- Grid View -->
        <div v-else-if="viewMode === 'grid'" class="teams-grid">
          <div 
            v-for="team in formattedCompetitionTeams" 
            :key="team._id" 
            class="team-card"
            :class="{ 
              'status-accepted': team.response_status === 'Accepted',
              'status-pending': team.response_status === 'Pending',
              'status-rejected': team.response_status === 'Rejected'
            }"
          >
            <div class="team-card-header">
              <div class="team-avatar-wrapper">
                <img 
                  :src="team.agency_id?.midThumbnail || require('@/assets/default.svg')" 
                  :alt="team.agency_id?.name"
                  class="team-avatar"
                />
                <div class="team-status-indicator" :class="getResponseStatusClass(team.response_status)"></div>
              </div>
              <div class="team-info">
                <h4 class="team-name">{{ team.agency_id?.name || 'Unknown Team' }}</h4>
                <span class="team-join-type">{{ team.join_type || 'N/A' }}</span>
              </div>
            </div>

            <div class="team-card-body">
              <div class="team-stat">
                <span class="stat-label">Status</span>
                <span class="stat-value status-badge" :class="getResponseStatusClass(team.response_status)">
                  {{ team.response_status || 'N/A' }}
                </span>
              </div>
              <div class="team-stat">
                <span class="stat-label">Posts</span>
                <span class="stat-value">{{ team.agency_post_count || 0 }}</span>
              </div>
              <div class="team-stat">
                <span class="stat-label">Scores</span>
                <span class="stat-value">{{ team.agency_total_points || 0 }}</span>
              </div>
            </div>

            <div class="team-card-footer">
              <span class="team-date">Joined: {{ formatDate(team.createdAt) }}</span>
              <button class="view-team-btn" @click="viewTeamDetails(team)">
                View Details →
              </button>
            </div>
          </div>
        </div>

        <!-- List View -->
        <div v-else class="teams-list">
          <div class="list-header">
            <span class="list-header-item">Team</span>
            <span class="list-header-item">Status</span>
            <span class="list-header-item">Posts</span>
            <span class="list-header-item">Scores</span>
            <span class="list-header-item">Joined</span>
            <!-- <span class="list-header-item">Action</span> -->
          </div>

          <div 
            v-for="team in formattedCompetitionTeams" 
            :key="team._id" 
            class="list-row"
            :class="{
              'row-accepted': team.response_status === 'Accepted',
              'row-pending': team.response_status === 'Pending',
              'row-rejected': team.response_status === 'Rejected'
            }"
          >
            <div class="list-item team-info-cell">
              <img 
                :src="team.agency_id?.midThumbnail || require('@/assets/default.svg')" 
                :alt="team.agency_id?.name"
                class="list-avatar"
              />
              <div>
                <div class="list-team-name">{{ team.agency_id?.name || 'Unknown Team' }}</div>
                <span class="list-join-type">{{ team.join_type || 'N/A' }}</span>
              </div>
            </div>
            <div class="list-item">
              <span class="status-badge small" :class="getResponseStatusClass(team.response_status)">
                {{ team.response_status || 'N/A' }}
              </span>
            </div>
            <div class="list-item">{{ team.agency_post_count || 0 }}</div>
            <div class="list-item">{{ team.agency_total_points || 0 }}</div>
            <div class="list-item">{{ formatDate(team.createdAt) }}</div>
            <!-- <div class="list-item">
              <button class="view-team-btn small" @click="viewTeamDetails(team)">
                View
              </button>
            </div> -->
          </div>
        </div>
      </div>
    </div>

    <!-- Toast Notification -->
    <div v-if="showToast" class="toast" :class="toastType">
      <span class="toast-icon">{{ toastIcon }}</span>
      <span class="toast-message">{{ toastMessage }}</span>
      <button class="toast-close" @click="showToast = false">×</button>
    </div>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "CompetitionDetail",

  data() {
    return {
      loading: false,
      competitionDetails: null,
      competitionTeams: [],
      search: "",
      viewMode: "list",
      showToast: false,
      toastMessage: "",
      toastType: "info",
      toastIcon: "ℹ️",
      timer: null,
    };
  },

  computed: {
    formattedCompetitionTeams() {
      let teams = this.competitionTeams || [];
      if (this.search && this.search.trim()) {
        const q = this.search.trim().toLowerCase();
        teams = teams.filter(team =>
          (team.agency_id?.name || "").toLowerCase().includes(q)
        );
      }
      return teams;
    },
  },

  methods: {
    ...mapActions({
      respectiveCompetition: "competition/respectiveCompetition",
    }),

    loadData() {
      this.loading = true;
      const params = {
        competition_id: this.$route.params.id,
      };

      this.respectiveCompetition(params)
        .then((res) => {
          this.loading = false;
          this.competitionDetails = res;
          this.competitionTeams = (res.competitionTeams || []).filter(
            (team) => team !== null && team !== undefined
          );
        })
        .catch((error) => {
          this.loading = false;
          this.showToastMessage(error.message || "Failed to load competition details", "error");
        });
    },

    formatDate(date) {
      if (!date) return "Not specified";
      const d = typeof date === "string" ? new Date(date) : date;
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    },

    getProgressPercentage() {
      if (!this.competitionDetails) return 0;
      const start = new Date(this.competitionDetails.start_date);
      const end = new Date(this.competitionDetails.end_date);
      const now = new Date();
      if (now < start) return 0;
      if (now > end) return 100;
      const total = end - start;
      const elapsed = now - start;
      return Math.round((elapsed / total) * 100);
    },

    getStatusClass(status) {
      const classes = {
        Ongoing: "status-ongoing",
        Upcoming: "status-upcoming",
        Completed: "status-completed",
      };
      return classes[status] || "status-default";
    },

    getResponseStatusClass(status) {
      const classes = {
        Accepted: "status-accepted",
        Pending: "status-pending",
        Rejected: "status-rejected",
      };
      return classes[status] || "status-default";
    },

    toggleView() {
      this.viewMode = this.viewMode === "grid" ? "list" : "grid";
    },

    viewTeamDetails(team) {
      this.showToastMessage(`Viewing ${team.agency_id?.name || 'Team'} details`, "info");
    },

    showToastMessage(message, type = "info") {
      this.toastMessage = message;
      this.toastType = type;
      this.toastIcon = type === "error" ? "❌" : type === "success" ? "✅" : "ℹ️";
      this.showToast = true;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        this.showToast = false;
      }, 3000);
    },
  },

  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
/* ============================================
   MAIN CONTAINER
============================================ */
.competition-detail-container {
  min-height: 100vh;
  padding: 24px;
  background: linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%);
  position: relative;
}

/* Background Effects */
.bg-effects {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
}

.glow-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  opacity: 0.15;
}

.glow-orb-1 {
  width: 400px;
  height: 400px;
  top: -100px;
  right: -100px;
  background: #4FC3F7;
}

.glow-orb-2 {
  width: 300px;
  height: 300px;
  bottom: -50px;
  left: -50px;
  background: #64B5F6;
}

.glow-orb-3 {
  width: 200px;
  height: 200px;
  top: 50%;
  left: 50%;
  background: #81D4FA;
  transform: translate(-50%, -50%);
}

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

/* ============================================
   BACK BUTTON
============================================ */
.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 12px;
  color: #1a2332;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-bottom: 24px;
}

.back-btn:hover {
  background: #fff;
  transform: translateX(-4px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
}

/* ============================================
   HERO SECTION
============================================ */
.hero-section {
  background: linear-gradient(135deg, #0a1628 0%, #1a3a5c 50%, #0d2b4a 100%);
  border-radius: 20px;
  padding: 48px;
  margin-bottom: 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(10, 22, 40, 0.3);
}

.hero-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: radial-gradient(circle at 30% 20%, rgba(79, 195, 247, 0.08) 0%, transparent 70%);
  pointer-events: none;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 16px;
}

.badge-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.badge-dot.status-ongoing {
  background: #4FC3F7;
  box-shadow: 0 0 12px rgba(79, 195, 247, 0.6);
}

.badge-dot.status-upcoming {
  background: #FFB74D;
  box-shadow: 0 0 12px rgba(255, 183, 77, 0.6);
}

.badge-dot.status-completed {
  background: #66BB6A;
  box-shadow: 0 0 12px rgba(102, 187, 106, 0.6);
}

.badge-dot.status-default {
  background: #78909C;
}

.hero-title {
  font-size: 2.8rem;
  font-weight: 700;
  color: #fff;
  margin: 0 0 12px 0;
  line-height: 1.1;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.hero-description {
  font-size: 1.05rem;
  color: rgba(255, 255, 255, 0.7);
  max-width: 600px;
  margin: 0 0 32px 0;
  line-height: 1.6;
}

.hero-stats {
  display: flex;
  align-items: center;
  gap: 32px;
  padding-top: 24px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.stat-icon {
  font-size: 24px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 12px;
}

.stat-value {
  font-size: 1.3rem;
  font-weight: 700;
  color: #fff;
}

.stat-label {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-divider {
  width: 1px;
  height: 40px;
  background: rgba(255, 255, 255, 0.1);
}

/* ============================================
   DATE RANGE CARD
============================================ */
.date-range-card {
  background: #fff;
  border-radius: 16px;
  padding: 24px 32px;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.date-range-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.date-label {
  font-size: 13px;
  font-weight: 600;
  color: #78909C;
}

.date-value {
  font-size: 14px;
  font-weight: 600;
  color: #1a2332;
}

.date-range-arrow {
  color: #78909C;
  font-size: 20px;
}

.date-range-progress {
  flex: 1;
  min-width: 150px;
}

.progress-track {
  width: 100%;
  height: 6px;
  background: #e8ecf1;
  border-radius: 4px;
  overflow: hidden;
  margin-bottom: 6px;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #4FC3F7, #64B5F6);
  border-radius: 4px;
  transition: width 0.6s ease;
}

.progress-label {
  font-size: 12px;
  font-weight: 500;
  color: #78909C;
}

/* ============================================
   TEAMS SECTION
============================================ */
.teams-section {
  background: #fff;
  border-radius: 20px;
  padding: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.section-title {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1a2332;
  margin: 0;
}

.section-count {
  font-size: 13px;
  font-weight: 600;
  color: #78909C;
  background: #f5f7fa;
  padding: 4px 12px;
  border-radius: 20px;
}

.header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

.search-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  background: #f5f7fa;
  border-radius: 10px;
  border: 1px solid transparent;
  transition: all 0.3s ease;
}

.search-box:focus-within {
  border-color: #4FC3F7;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(79, 195, 247, 0.1);
}

.search-icon {
  color: #78909C;
  flex-shrink: 0;
}

.search-input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px;
  color: #1a2332;
  width: 180px;
  font-family: inherit;
}

.search-input::placeholder {
  color: #b0bec5;
}

.view-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f5f7fa;
  border: 1px solid transparent;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  color: #78909C;
}

.view-toggle-btn:hover {
  background: #e8ecf1;
  color: #1a2332;
}

/* ============================================
   GRID VIEW
============================================ */
.teams-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.team-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8ecf1;
  padding: 20px;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.team-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: #e8ecf1;
  transition: all 0.3s ease;
}

.team-card.status-accepted::before {
  background: linear-gradient(90deg, #4FC3F7, #81D4FA);
}

.team-card.status-pending::before {
  background: linear-gradient(90deg, #FFB74D, #FFA726);
}

.team-card.status-rejected::before {
  background: linear-gradient(90deg, #EF5350, #E53935);
}

.team-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.08);
  border-color: transparent;
}

.team-card-header {
  display: flex;
  align-items: center;
  gap: 14px;
  margin-bottom: 16px;
}

.team-avatar-wrapper {
  position: relative;
}

.team-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #e8ecf1;
}

.team-status-indicator {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid #fff;
}

.team-status-indicator.status-accepted {
  background: #4FC3F7;
}

.team-status-indicator.status-pending {
  background: #FFB74D;
}

.team-status-indicator.status-rejected {
  background: #EF5350;
}

.team-status-indicator.status-default {
  background: #78909C;
}

.team-info {
  flex: 1;
  min-width: 0;
}

.team-name {
  font-size: 1rem;
  font-weight: 600;
  color: #1a2332;
  margin: 0 0 2px 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.team-join-type {
  font-size: 11px;
  font-weight: 600;
  color: #78909C;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.team-card-body {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  padding: 16px 0;
  border-top: 1px solid #f5f7fa;
  border-bottom: 1px solid #f5f7fa;
  margin-bottom: 16px;
}

.team-stat {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.team-stat .stat-label {
  font-size: 10px;
  font-weight: 600;
  color: #b0bec5;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.team-stat .stat-value {
  font-size: 1rem;
  font-weight: 700;
  color: #1a2332;
}

.status-badge {
  display: inline-flex;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.status-badge.status-accepted {
  background: rgba(79, 195, 247, 0.12);
  color: #0288D1;
}

.status-badge.status-pending {
  background: rgba(255, 183, 77, 0.12);
  color: #E65100;
}

.status-badge.status-rejected {
  background: rgba(239, 83, 80, 0.12);
  color: #C62828;
}

.status-badge.status-default {
  background: #f5f7fa;
  color: #78909C;
}

.team-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.team-date {
  font-size: 12px;
  color: #b0bec5;
}

.view-team-btn {
  padding: 6px 16px;
  background: #f5f7fa;
  border: none;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #1a2332;
  cursor: pointer;
  transition: all 0.3s ease;
}

.view-team-btn:hover {
  background: #1a2332;
  color: #fff;
}

.view-team-btn.small {
  padding: 4px 12px;
  font-size: 11px;
}

/* ============================================
   LIST VIEW
============================================ */
.teams-list {
  border: 1px solid #e8ecf1;
  border-radius: 12px;
  overflow: hidden;
}

.list-header {
  display: grid;
  grid-template-columns: 2fr 1fr 0.8fr 0.8fr 1.2fr 0.8fr;
  padding: 12px 16px;
  background: #f5f7fa;
  font-size: 11px;
  font-weight: 700;
  color: #78909C;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.list-row {
  display: grid;
  grid-template-columns: 2fr 1fr 0.8fr 0.8fr 1.2fr 0.8fr;
  padding: 12px 16px;
  border-top: 1px solid #f5f7fa;
  align-items: center;
  transition: background 0.3s ease;
}

.list-row:hover {
  background: #fafbfc;
}

.list-row.row-accepted {
  border-left: 3px solid #4FC3F7;
}

.list-row.row-pending {
  border-left: 3px solid #FFB74D;
}

.list-row.row-rejected {
  border-left: 3px solid #EF5350;
}

.list-item {
  font-size: 13px;
  color: #1a2332;
}

.team-info-cell {
  display: flex;
  align-items: center;
  gap: 12px;
}

.list-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.list-team-name {
  font-weight: 600;
  font-size: 13px;
  color: #1a2332;
}

.list-join-type {
  font-size: 10px;
  color: #b0bec5;
  font-weight: 600;
  text-transform: uppercase;
}

/* ============================================
   LOADING STATE
============================================ */
.loading-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
}

.team-skeleton {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e8ecf1;
  padding: 20px;
}

.skeleton-avatar {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: linear-gradient(90deg, #f5f7fa 25%, #e8ecf1 50%, #f5f7fa 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  margin-bottom: 12px;
}

.skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, #f5f7fa 25%, #e8ecf1 50%, #f5f7fa 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
  border-radius: 4px;
  margin-bottom: 8px;
}

.skeleton-line.short {
  width: 60%;
}

.skeleton-line.half {
  width: 80%;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* ============================================
   EMPTY STATE
============================================ */
.empty-state {
  text-align: center;
  padding: 60px 20px;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 1.2rem;
  color: #1a2332;
  margin: 0 0 8px 0;
}

.empty-state p {
  color: #78909C;
  margin: 0;
}

/* ============================================
   TOAST
============================================ */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: #1a2332;
  color: #fff;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  z-index: 1000;
  min-width: 280px;
  max-width: 420px;
  animation: slideUp 0.3s ease;
}

.toast.error {
  background: #c62828;
}

.toast.success {
  background: #2e7d32;
}

.toast.info {
  background: #0d47a1;
}

.toast-icon {
  font-size: 20px;
}

.toast-message {
  flex: 1;
  font-size: 14px;
}

.toast-close {
  background: none;
  border: none;
  color: rgba(255, 255, 255, 0.6);
  font-size: 24px;
  cursor: pointer;
  padding: 0 4px;
  transition: color 0.3s ease;
}

.toast-close:hover {
  color: #fff;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ============================================
   RESPONSIVE
============================================ */
@media (max-width: 1200px) {
  .teams-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}

@media (max-width: 992px) {
  .hero-title {
    font-size: 2.2rem;
  }

  .hero-stats {
    flex-wrap: wrap;
    gap: 16px;
  }

  .stat-divider {
    display: none;
  }

  .date-range-card {
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
  }

  .date-range-arrow {
    text-align: center;
  }

  .list-header,
  .list-row {
    grid-template-columns: 2fr 1fr 0.8fr 0.8fr 1.2fr 0.8fr;
    font-size: 12px;
  }
}

@media (max-width: 768px) {
  .competition-detail-container {
    padding: 16px;
  }

  .hero-section {
    padding: 32px 24px;
  }

  .hero-title {
    font-size: 1.8rem;
  }

  .hero-description {
    font-size: 0.95rem;
  }

  .teams-section {
    padding: 20px;
  }

  .section-header {
    flex-direction: column;
    align-items: stretch;
  }

  .header-right {
    flex-wrap: wrap;
  }

  .search-box {
    flex: 1;
  }

  .search-input {
    width: 100%;
  }

  .teams-grid {
    grid-template-columns: 1fr;
  }

  .list-header,
  .list-row {
    grid-template-columns: 1fr;
    gap: 8px;
    padding: 12px;
  }

  .list-header {
    display: none;
  }

  .list-row {
    border: 1px solid #f5f7fa;
    border-radius: 8px;
    margin-bottom: 8px;
  }

  .list-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 0;
  }

  .list-item::before {
    content: attr(data-label);
    font-weight: 600;
    color: #78909C;
    font-size: 11px;
    text-transform: uppercase;
  }

  .team-info-cell {
    flex-direction: column;
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .hero-section {
    padding: 24px 16px;
  }

  .hero-title {
    font-size: 1.5rem;
  }

  .hero-stats {
    flex-direction: column;
    align-items: stretch;
    gap: 12px;
  }

  .stat-item {
    justify-content: flex-start;
  }

  .teams-section {
    padding: 16px;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .team-card {
    padding: 16px;
  }

  .team-card-body {
    grid-template-columns: 1fr 1fr;
  }

  .toast {
    left: 16px;
    right: 16px;
    bottom: 16px;
    min-width: auto;
  }
}
</style>